<template>
  <div class="container mx-auto px-4 py-8">
    <div class="flex items-center justify-between mb-2">
      <h1 class="text-2xl font-bold text-gray-900 dark:text-white">Outbound Webhooks</h1>
      <button
        @click="openCreate"
        class="px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
      >+ New Webhook</button>
    </div>
    <p class="text-sm text-gray-500 dark:text-gray-400 mb-6">
      Receive JSON notifications when events happen in this tenant (e.g. <code>vuln.published</code>, <code>update.created</code>).
      Each delivery is signed with HMAC-SHA256 in the <code>X-S2C-Signature</code> header.
    </p>

    <div v-if="loading" class="flex items-center justify-center py-16">
      <div class="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 dark:border-gray-700 border-t-blue-500"></div>
    </div>

    <div v-else-if="hooks.length === 0" class="text-center py-16 text-gray-400 dark:text-gray-500 text-sm">
      No webhooks yet.
    </div>

    <div v-else class="space-y-3">
      <div
        v-for="h in hooks"
        :key="h.ID || h.id"
        class="card px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
      >
        <div class="min-w-0 flex-1">
          <div class="flex items-center gap-2 mb-1 flex-wrap">
            <p class="text-sm font-medium text-gray-900 dark:text-white truncate">{{ h.name }}</p>
            <span
              class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold"
              :class="h.enabled
                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'"
            >{{ h.enabled ? 'enabled' : 'disabled' }}</span>
            <span v-if="h.failure_count > 0" class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
              {{ h.failure_count }} failure{{ h.failure_count === 1 ? '' : 's' }}
            </span>
          </div>
          <p class="text-xs text-gray-500 dark:text-gray-400 font-mono truncate">{{ h.url }}</p>
          <div class="flex flex-wrap gap-2 mt-1">
            <span
              v-for="ev in parseEvents(h.events)"
              :key="ev"
              class="text-xs bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-1.5 py-0.5 rounded font-mono"
            >{{ ev }}</span>
            <span
              v-if="!h.events"
              class="text-xs bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-1.5 py-0.5 rounded font-mono"
              title="No event filter — delivers all event types"
            >* all events</span>
            <span v-if="h.last_delivered_at" class="text-xs text-gray-400 dark:text-gray-500">
              Last {{ h.last_status || '?' }} @ {{ formatDate(h.last_delivered_at) }}
            </span>
          </div>
          <p v-if="h.last_error" class="text-xs text-red-500 dark:text-red-400 mt-1 truncate" :title="h.last_error">
            {{ h.last_error }}
          </p>
        </div>

        <div class="flex items-center gap-2 flex-shrink-0">
          <button
            @click="toggleEnabled(h)"
            class="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >{{ h.enabled ? 'Disable' : 'Enable' }}</button>
          <button
            @click="openEdit(h)"
            class="px-3 py-1.5 text-xs font-medium rounded-md border border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
          >Edit</button>
          <button
            @click="deleteTarget = h"
            class="px-3 py-1.5 text-xs font-medium rounded-md border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
          >Delete</button>
        </div>
      </div>
    </div>

    <div v-if="showForm" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div class="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md">
        <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h2 class="text-lg font-semibold text-gray-900 dark:text-white">
            {{ editingId ? 'Edit Webhook' : 'New Webhook' }}
          </h2>
          <button @click="closeForm" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div class="px-6 py-5 space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Name <span class="text-red-500">*</span>
            </label>
            <input v-model="form.name" type="text" placeholder="e.g. Slack #security" class="input w-full" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Endpoint URL <span class="text-red-500">*</span>
            </label>
            <input v-model="form.url" type="url" placeholder="https://hooks.example.com/s2c" class="input w-full font-mono text-sm" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Event filter <span class="text-xs text-gray-400 ml-1">(comma-separated; leave empty for all)</span>
            </label>
            <input
              v-model="form.events"
              type="text"
              placeholder="vuln.published, sbom.finding.detected, sbom.license_violation"
              class="input w-full font-mono text-sm"
            />
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Known events: <code>vuln.published</code>, <code>update.created</code>, <code>vuln_report.received</code>,
              <code>sbom.finding.detected</code>, <code>sbom.license_violation</code>.
            </p>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Severity filter
              <span class="text-xs text-gray-400 ml-1">(applies to vulnerability events; empty = all)</span>
            </label>
            <div class="flex flex-wrap gap-2">
              <label
                v-for="sev in ['low','medium','high','critical']"
                :key="sev"
                class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-gray-300 dark:border-gray-600 cursor-pointer text-xs"
                :class="severitySelected(sev)
                  ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-400 dark:border-blue-600 text-blue-700 dark:text-blue-300'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'"
              >
                <input type="checkbox" :checked="severitySelected(sev)" @change="toggleSeverity(sev)" class="hidden" />
                {{ sev }}
              </label>
            </div>
          </div>

          <details class="rounded-md border border-gray-200 dark:border-gray-700 px-3 py-2">
            <summary class="cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300">
              Advanced: license filter & ignore lists
            </summary>
            <div class="mt-3 space-y-3">
              <div>
                <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  License filter
                  <span class="text-gray-400 ml-1">(SPDX substrings, comma-separated; empty = all license violations)</span>
                </label>
                <input
                  v-model="form.license_filters"
                  type="text"
                  placeholder="GPL, AGPL, SSPL"
                  class="input w-full font-mono text-xs"
                />
              </div>
              <div>
                <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Ignore CVEs <span class="text-gray-400 ml-1">(per-webhook; layered on tenant ignore list)</span>
                </label>
                <input
                  v-model="form.ignore_cves"
                  type="text"
                  placeholder="CVE-2024-12345, EUVD-2025-0001"
                  class="input w-full font-mono text-xs"
                />
              </div>
              <div>
                <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Ignore PURLs <span class="text-gray-400 ml-1">(substring match; e.g. <code>pkg:npm/lodash@</code>)</span>
                </label>
                <input
                  v-model="form.ignore_purls"
                  type="text"
                  placeholder="pkg:npm/lodash@, pkg:maven/com.example/legacy"
                  class="input w-full font-mono text-xs"
                />
              </div>
            </div>
          </details>
          <p v-if="formError" class="text-sm text-red-600 dark:text-red-400">{{ formError }}</p>
        </div>
        <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
          <button
            @click="closeForm"
            class="px-4 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          >Cancel</button>
          <button
            @click="submitForm"
            :disabled="submitting"
            class="px-4 py-2 text-sm rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >{{ submitting ? 'Saving…' : (editingId ? 'Save' : 'Create & Show Secret') }}</button>
        </div>
      </div>
    </div>

    <div v-if="newSecret" class="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div class="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg">
        <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 class="text-lg font-semibold text-gray-900 dark:text-white">Webhook Created</h2>
        </div>
        <div class="px-6 py-5 space-y-4">
          <div class="rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 px-4 py-3">
            <p class="text-sm font-semibold text-yellow-800 dark:text-yellow-300 mb-1">
              Copy this signing secret now — it will not be shown again.
            </p>
            <p class="text-xs text-yellow-700 dark:text-yellow-400">
              Use it on your receiver to verify the <code>X-S2C-Signature</code> HMAC-SHA256 header.
            </p>
          </div>
          <div class="relative">
            <pre class="bg-gray-900 text-green-400 text-xs rounded-lg px-4 py-3 overflow-x-auto break-all whitespace-pre-wrap select-all font-mono">{{ newSecret }}</pre>
            <button
              @click="copySecret"
              class="absolute top-2 right-2 px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-gray-200 rounded transition-colors"
            >{{ copied ? 'Copied!' : 'Copy' }}</button>
          </div>
        </div>
        <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <button @click="dismissSecret" class="px-4 py-2 text-sm rounded-md text-white bg-blue-600 hover:bg-blue-700">
            I've saved it, close
          </button>
        </div>
      </div>
    </div>

    <DeleteConfirmationModal
      :show="!!deleteTarget"
      title="Delete Webhook"
      :message="deleteTarget ? `Permanently delete '${deleteTarget.name}'? This cannot be undone.` : ''"
      confirm-text="Delete"
      @confirm="executeDelete"
      @cancel="deleteTarget = null"
    />
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useStore } from 'vuex'
import axios from 'axios'
import DeleteConfirmationModal from '../components/DeleteConfirmationModal.vue'

const getApiBase = () => {
  const envUrl = process.env.VUE_APP_API_URL || 'http://127.0.0.1:10000'
  const clean = envUrl.replace(/\/$/, '')
  return clean.endsWith('/api/v1') ? clean : `${clean}/api/v1`
}

export default {
  name: 'WebhooksView',
  components: { DeleteConfirmationModal },
  setup() {
    const store = useStore()
    const API = getApiBase()
    const tenantId = store.getters.currentTenantId
    const headers = () => ({ Authorization: `Bearer ${store.state.token}` })

    const hooks = ref([])
    const loading = ref(false)

    const showForm = ref(false)
    const editingId = ref(null)
    const submitting = ref(false)
    const formError = ref('')
    const form = ref({
      name: '', url: '', events: '',
      severities: '', license_filters: '', ignore_cves: '', ignore_purls: ''
    })

    const newSecret = ref('')
    const copied = ref(false)
    const deleteTarget = ref(null)

    const parseEvents = (s) => {
      if (!s) return []
      return s.split(',').map(x => x.trim()).filter(Boolean)
    }

    const formatDate = (d) => {
      if (!d) return '—'
      return new Date(d).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })
    }

    const fetchHooks = async () => {
      loading.value = true
      try {
        const { data } = await axios.get(
          `${API}/admin/tenants/${tenantId}/webhooks`,
          { headers: headers() }
        )
        hooks.value = data.webhooks ?? []
      } catch (e) {
        console.error('Failed to fetch webhooks', e)
      } finally {
        loading.value = false
      }
    }

    const openCreate = () => {
      editingId.value = null
      form.value = {
        name: '', url: '', events: '',
        severities: '', license_filters: '', ignore_cves: '', ignore_purls: ''
      }
      formError.value = ''
      showForm.value = true
    }

    const openEdit = (h) => {
      editingId.value = h.ID || h.id
      form.value = {
        name: h.name,
        url: h.url,
        events: h.events || '',
        severities: h.severities || '',
        license_filters: h.license_filters || '',
        ignore_cves: h.ignore_cves || '',
        ignore_purls: h.ignore_purls || ''
      }
      formError.value = ''
      showForm.value = true
    }

    const severitySelected = (sev) =>
      (form.value.severities || '').split(',').map(s => s.trim()).includes(sev)

    const toggleSeverity = (sev) => {
      const cur = (form.value.severities || '').split(',').map(s => s.trim()).filter(Boolean)
      const i = cur.indexOf(sev)
      if (i >= 0) cur.splice(i, 1)
      else cur.push(sev)
      form.value.severities = cur.join(',')
    }

    const closeForm = () => {
      showForm.value = false
    }

    const submitForm = async () => {
      if (!form.value.name || !form.value.url) {
        formError.value = 'Name and URL are required.'
        return
      }
      submitting.value = true
      formError.value = ''
      try {
        if (editingId.value) {
          await axios.put(
            `${API}/admin/tenants/${tenantId}/webhooks/${editingId.value}`,
            form.value,
            { headers: headers() }
          )
          await fetchHooks()
          closeForm()
        } else {
          const { data } = await axios.post(
            `${API}/admin/tenants/${tenantId}/webhooks`,
            form.value,
            { headers: headers() }
          )
          newSecret.value = data.secret
          closeForm()
          await fetchHooks()
        }
      } catch (e) {
        formError.value = e.response?.data?.error ?? 'Save failed'
      } finally {
        submitting.value = false
      }
    }

    const toggleEnabled = async (h) => {
      const id = h.ID || h.id
      try {
        await axios.put(
          `${API}/admin/tenants/${tenantId}/webhooks/${id}`,
          { enabled: !h.enabled },
          { headers: headers() }
        )
        h.enabled = !h.enabled
      } catch (e) {
        console.error('Toggle failed', e)
      }
    }

    const executeDelete = async () => {
      const id = deleteTarget.value?.ID || deleteTarget.value?.id
      if (!id) {
        deleteTarget.value = null
        return
      }
      try {
        await axios.delete(
          `${API}/admin/tenants/${tenantId}/webhooks/${id}`,
          { headers: headers() }
        )
        hooks.value = hooks.value.filter(h => (h.ID || h.id) !== id)
      } catch (e) {
        console.error('Delete failed', e)
      } finally {
        deleteTarget.value = null
      }
    }

    const copySecret = async () => {
      try {
        await navigator.clipboard.writeText(newSecret.value)
        copied.value = true
        setTimeout(() => { copied.value = false }, 2000)
      } catch (e) { /* clipboard unavailable */ }
    }

    const dismissSecret = () => {
      newSecret.value = ''
      copied.value = false
    }

    onMounted(fetchHooks)

    return {
      hooks, loading, showForm, editingId, submitting, formError, form,
      newSecret, copied, deleteTarget,
      parseEvents, formatDate,
      openCreate, openEdit, closeForm, submitForm,
      toggleEnabled, executeDelete, copySecret, dismissSecret,
      severitySelected, toggleSeverity
    }
  }
}
</script>
